import React, { memo } from 'react';
import { CalendarIcon, CheckCircleIcon } from '@heroicons/react/outline';

export enum PLATFORM {
  SHOPIFY = 'shopify',
  SHOPIFY_PLUS = 'shopify_plus',
  OTHER = 'other',
}

interface Step2Props {
  storeConnected: boolean;
  formData: {
    platform: PLATFORM;
    shopifyUrl: string;
    customAppInstallLink?: string;
    platformOther?: string;
    useCustomApp: boolean;
    enableCustomAppFlow?: boolean;
  };
  handlePlatform: (id: PLATFORM) => void;
  handleShopifyUrl: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePlatformOther: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeUseCustomApp: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCustomAppInstallLink: (e: React.ChangeEvent<HTMLInputElement>) => void;
  confirmDomain: () => void;
  editConfirmedDomain: () => void;
  generateInstallLinkError?: any;
  generateInstallLinkLoading: boolean;
  confirmedStoreUrl?: boolean;
}

const Step2: React.FC<Step2Props> = ({
  storeConnected,
  formData,
  handlePlatform,
  handleShopifyUrl,
  handlePlatformOther,
  onChangeUseCustomApp,
  handleCustomAppInstallLink,
  confirmDomain,
  editConfirmedDomain,
  generateInstallLinkError,
  generateInstallLinkLoading,
  confirmedStoreUrl,
}) => {
  const platforms = [
    {
      id: PLATFORM.SHOPIFY,
      label: 'Shopify',
      icon: '/shopify-icon.png',
    },
    {
      id: PLATFORM.SHOPIFY_PLUS,
      label: 'Shopify Plus',
      icon: '/shopify-plus-icon-partner.png',
      styles: 'w-[180px]',
    },
    {
      id: PLATFORM.OTHER,
      label: 'Other',
      icon: '/other-platform-icon.png',
    },
  ];

  return (
    <div>
      <div className="flex justify-start gap-2 mb-5 mx-auto w-[85%]">
        <div
          className="mr-5 w-[200px]"
          dangerouslySetInnerHTML={{
            __html: `<div style="position: relative; box-sizing: content-box; width: 100%; aspect-ratio: 1.7777777777777777;"><iframe src="https://guides.shipaid.com/embed/cmcwe6gui1f2w9empfkcqvu7k?embed_v=2" loading="lazy" title="Connect Your Store Platform" allow="clipboard-write" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 200px; height: 150px"></iframe></div>`,
          }}
        />
        <div className="text-left">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Connect Your Store Platform
          </h2>
          <p className="text-gray-600 mb-8">
            Choose the platform that powers your store.
          </p>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Which platform powers your store?
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {platforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => handlePlatform(platform.id)}
              disabled={storeConnected}
              className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                formData.platform === platform.id
                  ? 'border-[#2563eb] bg-blue-50'
                  : 'border-gray-200 hover:border-[#2563eb] hover:bg-blue-50'
              } ${
                storeConnected && formData.platform !== platform.id
                  ? 'opacity-[0.5]'
                  : ''
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">
                  <img
                    src={platform.icon}
                    alt={platform.label}
                    className={`w-16 m-auto ${platform.styles || ''}`}
                  />
                </div>
                <h4 className="font-semibold text-gray-900">
                  {platform.label}
                </h4>
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {formData.platform !== PLATFORM.OTHER && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your store’s Shopify URL
              </label>
              <div className="flex gap-2 justify-start items-center">
                <div className="w-full relative">
                  {
                    <div className="w-full flex items-center justify-start">
                      <input
                        type="url"
                        value={formData.shopifyUrl}
                        onChange={handleShopifyUrl}
                        disabled={storeConnected || confirmedStoreUrl}
                        placeholder="https://your-store.myshopify.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                      />
                    </div>
                  }
                  {storeConnected && (
                    <div className="flex my-2 text-green-400 absolute mt-1 items-center">
                      <CheckCircleIcon className="w-4 h-4 text-green-400 mr-2" />
                      <span>
                        Store connected, please click next button below to
                        proceed
                      </span>
                    </div>
                  )}
                  {confirmedStoreUrl && (
                    <div className="flex my-2 text-green-400 absolute mt-1 items-center">
                      <CheckCircleIcon className="w-4 h-4 text-green-400 mr-2" />{' '}
                      <span>Store confirmed, please click install below</span>
                    </div>
                  )}
                </div>
                {!storeConnected &&
                  (confirmedStoreUrl ? (
                    <button
                      onClick={editConfirmedDomain}
                      className={`w-[100px] bg-yellow-500 py-[12px] px-[10px] text-white hover:bg-yellow-600 rounded-md`}
                    >
                      Edit
                    </button>
                  ) : (
                    <button
                      disabled={
                        !formData.shopifyUrl.includes('.myshopify.com') ||
                        generateInstallLinkLoading
                      }
                      onClick={confirmDomain}
                      className={`w-[100px] bg-[#2563eb] py-[12px] px-[10px] text-white hover:bg-blue-700 rounded-md ${
                        !formData.shopifyUrl.includes('.myshopify.com')
                          ? 'opacity-[0.5]'
                          : ''
                      }`}
                    >
                      {generateInstallLinkLoading ? 'Wait...' : 'Confirm'}
                    </button>
                  ))}
              </div>
              {generateInstallLinkError && (
                <span className="my-2 text-red-400 text-md">
                  {generateInstallLinkError}
                </span>
              )}
            </div>
          )}

          {formData.platform === PLATFORM.OTHER && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your platform
              </label>
              <input
                type="text"
                value={formData.platformOther || ''}
                onChange={handlePlatformOther}
                placeholder="Eg: WooCommerce or BigCommerce or Magento"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
              />
            </div>
          )}
        </div>

        {formData.platform !== PLATFORM.OTHER ? (
          <>
            <div
              className={`flex items-center ${
                confirmedStoreUrl || storeConnected ? 'mt-10' : 'mt-5'
              } mb-5`}
            >
              <input
                type="checkbox"
                id="useCustomApp"
                checked={formData.useCustomApp}
                onChange={onChangeUseCustomApp}
                disabled={storeConnected || formData.enableCustomAppFlow}
                className="h-4 w-4 text-[#2563eb] focus:ring-[#2563eb] border-gray-300 rounded"
              />
              <label
                htmlFor="useCustomApp"
                className="ml-2 text-sm text-gray-700"
              >
                Use a Custom App instead
              </label>
            </div>

            {formData.useCustomApp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your custom app install link
                </label>
                <input
                  type="text"
                  value={formData.customAppInstallLink || ''}
                  onChange={handleCustomAppInstallLink}
                  placeholder="Eg: Install Link"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                />
              </div>
            )}
          </>
        ) : (
          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              Looking for integration with other platforms?
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Book a free consultation call with our experts
            </p>
            <div className="text-center">
              <button
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                onClick={() =>
                  window.open('https://calendly.com/shipaid/onboarding')
                }
              >
                <CalendarIcon className="mr-2 w-8 h-8" />
                Book Free Consultation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(Step2);
